import { useCurrentUser } from '@do/current-user';
import { isAMD } from '../utils';
import { amplitudeExperiment } from './AmplitudeExperimentSDK';
import { useEffect, useState } from 'react';

/**
 * Hook for fetching experiment variants and automatically tracking exposure events.
 * Use this when you need to display experiment variants in the UI and want exposure tracking.
 * !!Important!! If you only need to check variant assignment without exposure tracking, use `useExperimentLookup` instead.
 *
 * @param {string} experimentKey - The unique identifier for the experiment
 * @param {Object.<string, any>} [audienceAttributes] - Optional user attributes for targeting/segmentation
 * @returns {Object} An object containing:
 *   - variant: string | null - The assigned experiment variant or null if not assigned/AMD environment
 *   - loading: boolean - Whether the variant fetch and exposure tracking is still in progress
 * @example <caption>Get experiment variant and track exposure without audience attributes</caption>
 * const { variant, loading } = useDisplayExperiment({ experimentKey: 'grow-123_droplets_experiment' });
 * @example <caption>Get experiment variant and track exposure with audience attributes</caption>
 * const { variant, loading } = useDisplayExperiment({
 *   experimentKey: 'grow-123_droplets_experiment',
 *   audienceAttributes: { has_droplets: true }
 * });
 **/

interface useDisplayExperimentProps {
  experimentKey: string;
  audienceAttributes?: { [name: string]: any };
}

interface useDisplayExperimentResult {
  variant: string | null;
  loading: boolean;
}

export const useDisplayExperiment = ({
  experimentKey,
  audienceAttributes
}: useDisplayExperimentProps): useDisplayExperimentResult => {
  const { user, team } = useCurrentUser();
  const [variant, setVariant] = useState(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setLoading(true);
    setVariant(null);

    if (isAMD()) {
      setLoading(false);
      return;
    }

    const fetchAmplitudeFlagWithAudience = async (): Promise<void> => {
      try {
        // (1) Fetch variants and await the promise result.
        await amplitudeExperiment.fetch({
          user_id: user.uuid,
          user_properties: audienceAttributes,
          groups: {
            team_id: [team?.uuid]
          }
        });

        // (2) Get specific variant after fetching
        const updatedVariant = amplitudeExperiment.variant(experimentKey);

        // (3) Skip exposure if no variant is assigned
        if (!updatedVariant?.value) {
          return;
        }

        // (4) Send exposure event to Amplitude - This let's Amplitude know that the user was exposed to this variant.
        // This is essential for experiment analysis queries on primary and secondary success metrics.
        amplitudeExperiment.exposure(experimentKey);

        setVariant(updatedVariant?.value);
      } finally {
        setLoading(false);
      }
    };

    fetchAmplitudeFlagWithAudience();
  }, [experimentKey, team, user]);

  return { variant, loading };
};
